<script setup lang="ts">
import { getTimeZones } from '@vvo/tzdb';
import { ref, watch, watchEffect } from 'vue';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../select';

interface TimeZoneOption {
  label: string;
  value: string;
  currentTime: string;
}

const browserTimeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'America/New_York';

let defaultTimeZone = browserTimeZone;
const timeZones = getTimeZones()
  .filter((timeZone) => {
    const isNorthAmerican = timeZone.continentName === 'North America';
    if (isNorthAmerican && timeZone.group.includes(browserTimeZone)) {
      defaultTimeZone = timeZone.name;
    }
    return isNorthAmerican;
  })
  .map<TimeZoneOption>((timeZone) => ({
    label: timeZone.alternativeName,
    value: timeZone.name,
    currentTime: new Intl.DateTimeFormat(undefined, {
      timeStyle: 'short',
      timeZone: timeZone.name,
    }).format(new Date()),
  }));

const props = defineProps<{
  modelValue?: string;
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void;
}>();

const timeZone = ref(props.modelValue ?? defaultTimeZone);

watchEffect(() => {
  timeZone.value = props.modelValue ?? defaultTimeZone;
});

watch(timeZone, (value) => {
  emit('update:modelValue', value);
});
</script>

<template>
  <Select v-model="timeZone">
    <SelectTrigger>
      <SelectValue :placeholder="defaultTimeZone" />
    </SelectTrigger>
    <SelectContent>
      <SelectItem v-for="timeZoneOption in timeZones" :key="timeZoneOption.value" :value="timeZoneOption.value">
        {{ timeZoneOption.label }}
        <template #detail>
          <small class="text-muted-foreground ml-auto shrink-0">{{ timeZoneOption.currentTime }}</small>
        </template>
      </SelectItem>
    </SelectContent>
  </Select>
</template>
